import request from "../utils/request";
import { normalizePaginationInteger } from "./pagination";
import type { StudySourceConnectionType } from "./studySource";

export interface StudyCraUser {
  assignmentId?: string;
  addedAt?: string;
  id: string;
  name: string;
  email: string;
  isAssigned?: boolean;
}

export interface StudySourceMember extends StudyCraUser {
  roles: string;
  connectionTypes: StudySourceConnectionType[];
  hasDg?: boolean;
}

export interface AssignedCraDisplayMember extends StudySourceMember {
  manuallyAssigned: boolean;
}

export type StudyMemberSourceFilter = "all" | "auto" | "manual" | "teams" | "sharepoint" | "dg";

export interface StudyMember {
  assignmentId?: string;
  addedAt?: string;
  id: string;
  name: string;
  email: string;
  manuallyAssigned: boolean;
  connectionTypes: StudySourceConnectionType[];
  /** 是否来自 Distribution Group 同步 */
  hasDg: boolean;
}

export interface StudyMemberSearchResult {
  count: number;
  totalCount: number;
  page: number;
  limit: number;
  data: StudyMember[];
}

export interface Study {
  id: string;
  name: string;
  code: string;
  assignedCras: StudyCraUser[];
  sourceMembers: StudySourceMember[];
  status: string;
  createdAt: string;
  reports: number;
}

type StudyCraCollections = Pick<Study, "assignedCras" | "sourceMembers">;

interface StudyCraUserWire {
  AssignmentId?: string;
  assignmentId?: string;
  AddedAt?: string;
  addedAt?: string;
  Id?: string;
  Name?: string;
  Email?: string;
  id?: string;
  name?: string;
  email?: string;
  IsAssigned?: boolean;
  isAssigned?: boolean;
}

interface StudySourceMemberWire extends StudyCraUserWire {
  Roles?: string;
  roles?: string;
  ConnectionTypes?: Array<number | string>;
  connectionTypes?: Array<number | string>;
  HasDg?: boolean;
  hasDg?: boolean;
}

interface StudyWire {
  Id?: string;
  Name?: string;
  Code?: string;
  AssignedCras?: StudyCraUserWire[];
  SourceMembers?: StudySourceMemberWire[];
  Status?: string;
  CreatedAt?: string;
  Reports?: number;
  id?: string;
  name?: string;
  code?: string;
  assignedCras?: StudyCraUserWire[];
  sourceMembers?: StudySourceMemberWire[];
  status?: string;
  createdAt?: string;
  reports?: number;
}

interface StudySearchWire {
  Count?: number | string;
  Page?: number | string;
  Limit?: number | string;
  Data?: StudyWire[];
  count?: number | string;
  page?: number | string;
  limit?: number | string;
  data?: StudyWire[];
}

interface StudyMemberWire {
  AssignmentId?: string;
  assignmentId?: string;
  AddedAt?: string;
  addedAt?: string;
  Id?: string;
  id?: string;
  Name?: string;
  name?: string;
  Email?: string;
  email?: string;
  ManuallyAssigned?: boolean;
  manuallyAssigned?: boolean;
  ConnectionTypes?: Array<number | string>;
  connectionTypes?: Array<number | string>;
  HasDg?: boolean;
  hasDg?: boolean;
}

interface StudyMemberSearchWire {
  Count?: number | string;
  count?: number | string;
  TotalCount?: number | string;
  totalCount?: number | string;
  Page?: number | string;
  page?: number | string;
  Limit?: number | string;
  limit?: number | string;
  Data?: StudyMemberWire[];
  data?: StudyMemberWire[];
}

export interface StudySearchResult {
  count: number;
  page: number;
  limit: number;
  data: Study[];
}

export interface StudySaveInput {
  name: string;
  code: string;
  /** 不传表示不修改关联 CRA（编辑时保留原有关联） */
  assignedCraIds?: string[];
}

const BASE = "/_Admin/StudyManagement";

function normalizeCra(user: StudyCraUserWire): StudyCraUser {
  const normalized: StudyCraUser = {
    id: user.Id ?? user.id ?? "",
    name: user.Name ?? user.name ?? "",
    email: user.Email ?? user.email ?? "",
  };
  const assignmentId = user.AssignmentId ?? user.assignmentId;
  const addedAt = user.AddedAt ?? user.addedAt;
  const isAssigned = user.IsAssigned ?? user.isAssigned;
  return {
    ...normalized,
    ...(assignmentId ? { assignmentId } : {}),
    ...(addedAt ? { addedAt } : {}),
    ...(isAssigned === undefined ? {} : { isAssigned }),
  };
}

function normalizeSourceMember(member: StudySourceMemberWire): StudySourceMember {
  const connectionTypes = (member.ConnectionTypes ?? member.connectionTypes ?? [])
    .map((value) => (typeof value === "string" ? Number(value) : value))
    .filter((value): value is StudySourceConnectionType => value === 0 || value === 1);
  return {
    ...normalizeCra(member),
    roles: member.Roles ?? member.roles ?? "",
    connectionTypes,
    hasDg: member.HasDg ?? member.hasDg ?? false,
  };
}

function normalizeStudy(study: StudyWire): Study {
  return {
    id: study.Id ?? study.id ?? "",
    name: study.Name ?? study.name ?? "",
    code: study.Code ?? study.code ?? "",
    assignedCras: (study.AssignedCras ?? study.assignedCras ?? []).map(normalizeCra),
    sourceMembers: (study.SourceMembers ?? study.sourceMembers ?? []).map(normalizeSourceMember),
    status: study.Status ?? study.status ?? "Active",
    createdAt: study.CreatedAt ?? study.createdAt ?? "",
    reports: study.Reports ?? study.reports ?? 0,
  };
}

function normalizeStudyMember(member: StudyMemberWire): StudyMember {
  const connectionTypes = (member.ConnectionTypes ?? member.connectionTypes ?? [])
    .map((value) => (typeof value === "string" ? Number(value) : value))
    .filter((value): value is StudySourceConnectionType => value === 0 || value === 1);
  const assignmentId = member.AssignmentId ?? member.assignmentId;
  const addedAt = member.AddedAt ?? member.addedAt;
  return {
    id: member.Id ?? member.id ?? "",
    name: member.Name ?? member.name ?? "",
    email: member.Email ?? member.email ?? "",
    ...(assignmentId ? { assignmentId } : {}),
    ...(addedAt ? { addedAt } : {}),
    manuallyAssigned: member.ManuallyAssigned ?? member.manuallyAssigned ?? false,
    connectionTypes,
    hasDg: member.HasDg ?? member.hasDg ?? false,
  };
}

function normalizeCraIdentity(user: StudyCraUser, fallbackIndex: number): string {
  const email = user.email.trim().toLowerCase();
  if (email) return `email:${email}`;

  const id = user.id.trim().toLowerCase();
  if (id) return `id:${id}`;

  const name = user.name.trim().toLowerCase();
  return name ? `name:${name}` : `anonymous:${fallbackIndex}`;
}

/** 是否具有自动来源；与 Manual 独立判断，同一成员可以同时命中两者。 */
export function hasAutomaticStudySource(member: {
  connectionTypes: readonly StudySourceConnectionType[];
  hasDg?: boolean;
}): boolean {
  return member.connectionTypes.length > 0 || member.hasDg === true;
}

/** 合并 Study 分配关系与自动成员快照，并保留全部来源。 */
export function mergeAssignedCraMembers(study: StudyCraCollections): AssignedCraDisplayMember[] {
  const merged: AssignedCraDisplayMember[] = [];
  const indexByIdentity = new Map<string, number>();

  const append = (member: AssignedCraDisplayMember) => {
    const identity = normalizeCraIdentity(member, merged.length);
    const existingIndex = indexByIdentity.get(identity);
    if (existingIndex === undefined) {
      indexByIdentity.set(identity, merged.length);
      merged.push({
        ...member,
        connectionTypes: [...member.connectionTypes],
      });
      return;
    }

    const existing = merged[existingIndex];
    const roles = [...existing.roles.split(";"), ...member.roles.split(";")]
      .map((role) => role.trim())
      .filter(Boolean)
      .filter(
        (role, index, values) =>
          values.findIndex((candidate) => candidate.toLowerCase() === role.toLowerCase()) === index,
      );
    const manualAddedAt = existing.manuallyAssigned
      ? existing.addedAt
      : member.manuallyAssigned
        ? member.addedAt
        : undefined;
    const addedAt = manualAddedAt || existing.addedAt || member.addedAt;
    merged[existingIndex] = {
      id: existing.id || member.id,
      name: existing.name || member.name,
      email: existing.email || member.email,
      ...(existing.assignmentId || member.assignmentId
        ? { assignmentId: existing.assignmentId || member.assignmentId }
        : {}),
      ...(addedAt ? { addedAt } : {}),
      roles: roles.join(";"),
      connectionTypes: Array.from(
        new Set([...existing.connectionTypes, ...member.connectionTypes]),
      ),
      manuallyAssigned: existing.manuallyAssigned || member.manuallyAssigned,
      hasDg: existing.hasDg === true || member.hasDg === true,
    };
  };

  study.sourceMembers.forEach((member) =>
    append({
      ...member,
      manuallyAssigned: false,
    }),
  );
  study.assignedCras.forEach((cra) =>
    append({
      ...cra,
      roles: "",
      connectionTypes: [],
      manuallyAssigned: true,
    }),
  );

  return merged;
}

export async function searchStudies(keyword = "", page = 1, limit = 20) {
  const response = await request.post<StudySearchWire>(`${BASE}/Search`, {
    Keyword: keyword || undefined,
    Page: page,
    Limit: limit,
  });
  const body = response.data;
  return {
    count: normalizePaginationInteger(body.Count ?? body.count, 0, 0),
    page: normalizePaginationInteger(body.Page ?? body.page, page, 1),
    limit: normalizePaginationInteger(body.Limit ?? body.limit, limit, 1),
    data: (body.Data ?? body.data ?? []).map(normalizeStudy),
  } satisfies StudySearchResult;
}

export async function listAssignableCras(studyId: string, keyword: string) {
  const response = await request.get<StudyCraUserWire[]>(`${BASE}/AssignableUsers`, {
    params: { studyId, keyword },
  });
  return (response.data ?? []).map(normalizeCra);
}

export async function listStudyMembers(
  studyId: string,
  options: {
    keyword?: string;
    source?: StudyMemberSourceFilter;
    page?: number;
    limit?: number;
  } = {},
) {
  const page = options.page ?? 1;
  const limit = options.limit ?? 10;
  const response = await request.get<StudyMemberSearchWire>(
    `${BASE}/${encodeURIComponent(studyId)}/Members`,
    {
      params: {
        keyword: options.keyword || undefined,
        source: options.source ?? "all",
        page,
        limit,
      },
    },
  );
  const body = response.data;
  return {
    count: normalizePaginationInteger(body.Count ?? body.count, 0, 0),
    totalCount: normalizePaginationInteger(body.TotalCount ?? body.totalCount, 0, 0),
    page: normalizePaginationInteger(body.Page ?? body.page, page, 1),
    limit: normalizePaginationInteger(body.Limit ?? body.limit, limit, 1),
    data: (body.Data ?? body.data ?? []).map(normalizeStudyMember),
  } satisfies StudyMemberSearchResult;
}

export async function addStudyCraAssignments(studyId: string, userIds: string[]) {
  await request.post(`${BASE}/${encodeURIComponent(studyId)}/Assignments`, {
    UserIds: userIds,
  });
}

export async function removeStudyCraAssignment(studyId: string, assignmentId: string) {
  await request.delete(
    `${BASE}/${encodeURIComponent(studyId)}/Assignments/${encodeURIComponent(assignmentId)}`,
  );
}

export async function getStudy(id: string) {
  const response = await request.get<StudyWire>(`${BASE}/${encodeURIComponent(id)}`);
  return normalizeStudy(response.data);
}

export async function createStudy(input: StudySaveInput) {
  const response = await request.post<StudyWire>(`${BASE}/Add`, {
    Name: input.name,
    Code: input.code,
    ...(input.assignedCraIds ? { AssignedCraIds: input.assignedCraIds } : {}),
  });
  return normalizeStudy(response.data);
}

export async function updateStudy(id: string, input: StudySaveInput) {
  const response = await request.put<StudyWire>(`${BASE}/Edit`, {
    Id: id,
    Name: input.name,
    Code: input.code,
    ...(input.assignedCraIds ? { AssignedCraIds: input.assignedCraIds } : {}),
  });
  return normalizeStudy(response.data);
}

export function getStudyErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === "object") {
    const message = (data as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  if (typeof data === "string" && data.trim()) return data;
  return error instanceof Error && error.message ? error.message : fallback;
}
